import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BarChart3, Bot, Palette, Info, type LucideIcon } from 'lucide-react';
import { Button, Callout } from '@/shared/ui';
import type { StrategyDimension, CampaignsDimension } from '../hooks/useAdsState';
import styles from './ZeroStateOnboarding.module.css';

const FEATURES: Array<{ icon: LucideIcon; title: string; description: string }> = [
  {
    icon: Search,
    title: 'Instant Ad Audit',
    description: 'AI health score with actionable fixes in 60 seconds',
  },
  {
    icon: BarChart3,
    title: 'Unified Performance',
    description: 'All channels in one dashboard',
  },
  {
    icon: Bot,
    title: 'AI Campaign Creation',
    description: 'Strategy-driven campaigns with keywords & targeting',
  },
  {
    icon: Palette,
    title: 'Managed Creative',
    description: 'Ad creative reviewed within 24 hours',
  },
];

interface Props {
  onConnectGoogle: () => void;
  googleLoading?: boolean;
  strategy: StrategyDimension;
  campaigns: CampaignsDimension;
  /** True once at least one ad account is connected — hides the connect card and
   *  drops the "connect your accounts" framing, since the only thing left is to
   *  finish the strategy and let recommendations flow in. */
  accountsConnected?: boolean;
}

export function ZeroStateOnboarding({
  onConnectGoogle,
  googleLoading,
  strategy,
  campaigns,
  accountsConnected = false,
}: Props) {
  const navigate = useNavigate();

  // An active strategy needs no nudge — the only thing left is to connect ads. But
  // an active strategy with no campaigns shouldn't happen (it should have produced
  // recommendations), so surface a "contact us" notice instead. A draft in progress
  // gets a "continue" CTA; otherwise prompt to create one.
  const showS3Error = strategy === 'active' && campaigns === 'none';
  const showStrategyCta = strategy !== 'active';
  const isInProgress = strategy === 'in_progress';

  return (
    <div className={styles.root}>
      <div className={styles.eyebrow}>Ad Campaigns</div>
      <h1 className={styles.headline}>
        AI-Powered Advertising
        <br />
        for Your Practice
      </h1>
      <p className={styles.subhead}>
        {accountsConnected
          ? 'Get instant performance insights, AI-powered audits, and optimization recommendations.'
          : 'Connect your ad accounts to get instant performance insights, AI-powered audits, and optimization recommendations.'}
      </p>

      <div className={styles.featuresCard}>
        <div className={styles.featuresTitle}>What you'll get</div>
        <div className={styles.featuresGrid}>
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className={styles.feature}>
                <span className={styles.featureIcon}>
                  <Icon size={18} strokeWidth={1.75} aria-hidden />
                </span>
                <div>
                  <div className={styles.featureTitle}>{f.title}</div>
                  <div className={styles.featureDesc}>{f.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!accountsConnected && (
        <div className={styles.connectCard}>
          <div className={styles.connectTitle}>Connect Your Ad Accounts</div>
          <div className={styles.connectHint}>
            We'll pull your existing campaigns and run an instant AI audit. Meta Ads is connected by your agency.
          </div>
          <div className={styles.connectActions}>
            <Button variant="primary" onClick={onConnectGoogle} disabled={googleLoading}>
              {googleLoading ? 'Connecting…' : 'Connect Google Ads'}
            </Button>
          </div>
        </div>
      )}

      {showStrategyCta && (
        <div className={styles.strategyCard}>
          <div className={styles.strategyTitle}>No active campaigns yet</div>
          <div className={styles.strategyHint}>
            {isInProgress
              ? 'Finish your strategy and PracticeNova will recommend campaigns tailored to your practice.'
              : 'Create a marketing strategy and PracticeNova will recommend campaigns tailored to your practice.'}
          </div>
          <div className={styles.strategyActions}>
            <Button variant="secondary" size="sm" onClick={() => navigate('/strategy')}>
              {isInProgress ? 'Complete strategy →' : 'Create marketing strategy →'}
            </Button>
          </div>
        </div>
      )}

      {showS3Error && (
        <Callout variant="warning" icon={Info} title="No campaign recommendations yet">
          Your strategy is active, but we haven't generated campaign recommendations for it yet.
          Reach out to PracticeNova and we'll get them set up.
        </Callout>
      )}

      <div className={styles.footer}>
        More channels coming: TikTok · Reddit · Nextdoor · LinkedIn · Video
      </div>
    </div>
  );
}
